import React, { useEffect, useState, useRef } from "react";
import { Upload, Trash2, Save, Image, RefreshCw, DollarSign } from "lucide-react";
import { toast } from "react-hot-toast";
import useImageUpload from "../../hooks/useImageUpload";
import {
  getPlatform,
  updatePlatformLogo,
  deletePlatformLogo,
  updateApplePassLogo,
  deleteApplePassLogo,
  updatePlatformCurrency,
} from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { getCurrencySymbol } from "../../utils/currency";

const SUPPORTED_CURRENCIES = [
  { code: "USD", name: "US Dollar" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "NZD", name: "New Zealand Dollar" },
  { code: "INR", name: "Indian Rupee" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "CNY", name: "Chinese Yuan" },
  { code: "MXN", name: "Mexican Peso" },
  { code: "BRL", name: "Brazilian Real" },
  { code: "CHF", name: "Swiss Franc" },
  { code: "SGD", name: "Singapore Dollar" },
  { code: "HKD", name: "Hong Kong Dollar" },
  { code: "AED", name: "UAE Dirham" },
  { code: "SAR", name: "Saudi Riyal" },
  { code: "ZAR", name: "South African Rand" },
  { code: "PKR", name: "Pakistani Rupee" },
  { code: "PHP", name: "Philippine Peso" },
  { code: "THB", name: "Thai Baht" },
  { code: "IDR", name: "Indonesian Rupiah" },
  { code: "MYR", name: "Malaysian Ringgit" },
  { code: "KRW", name: "South Korean Won" },
];

interface Platform {
  _id: string;
  logo: string;
  applePassLogo: string;
  currency?: string;
}

const PlatformLogo: React.FC = () => {
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [applePassPreviewUrl, setApplePassPreviewUrl] = useState<string>("");
  const [selectedCurrency, setSelectedCurrency] = useState<string>("USD");
  const [savingCurrency, setSavingCurrency] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setCurrency } = useAuth();

  const { file, setFile, uploading, uploadImage } = useImageUpload();

  useEffect(() => {
    fetchPlatform();
  }, []);

  const fetchPlatform = async () => {
    setLoading(true);
    try {
      const res = await getPlatform();
      console.log("Platform response:", res.data);
      setPlatform(res.data.data);
      setPreviewUrl(res.data.data?.logo || "");
      setApplePassPreviewUrl(res.data.data?.applePassLogo || "");
      setSelectedCurrency(res.data.data?.currency || "USD");
    } catch (err: any) {
      console.error("Failed to load platform:", err);
      console.error("Error response:", err.response?.data);
      toast.error("Failed to load platform settings");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setFile(selectedFile);
    // Create preview URL
    const preview = URL.createObjectURL(selectedFile);
    setPreviewUrl(preview);
  };

  const handleApplePassFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setFile(selectedFile);
    // Create preview URL
    const preview = URL.createObjectURL(selectedFile);
    setApplePassPreviewUrl(preview);
  };

  const handleSave = async () => {
    if (!file && !platform?.logo) {
      toast.error("Please select an image first");
      return;
    }

    setSaving(true);
    try {
      // Upload image using the hook function - returns existing URL if no file selected
      console.log("Starting upload, file:", file?.name);
      const logoUrl = await uploadImage(platform?.logo || "");
      console.log("Upload complete, URL:", logoUrl);

      if (!logoUrl) {
        toast.error("Failed to upload image");
        setSaving(false);
        return;
      }

      // Send the image URL to backend
      console.log("Sending logo URL to backend:", logoUrl);
      const response = await updatePlatformLogo(logoUrl);
      console.log("Backend response:", response.data);

      toast.success("Logo updated successfully");
      setPreviewUrl(logoUrl);
      fetchPlatform();
    } catch (err: any) {
      console.error("Failed to save logo:", err);
      console.error("Error response:", err.response?.data);
      toast.error("Failed to update logo");
    } finally {
      setSaving(false);
    }
  };

  const saveApplePassLogo = async () => {
    if (!file && !platform?.logo) {
      toast.error("Please select an image first");
      return;
    }

    setSaving(true);
    try {
      // Upload image using the hook function - returns existing URL if no file selected
      console.log("Starting upload, file:", file?.name);
      const logoUrl = await uploadImage(platform?.logo || "");
      console.log("Upload complete, URL:", logoUrl);

      if (!logoUrl) {
        toast.error("Failed to upload image");
        setSaving(false);
        return;
      }

      // Send the image URL to backend
      console.log("Sending logo URL to backend:", logoUrl);
      const response = await updateApplePassLogo(logoUrl);
      console.log("Backend response:", response.data);

      toast.success("Logo updated successfully");
      setPreviewUrl(logoUrl);
      fetchPlatform();
    } catch (err: any) {
      console.error("Failed to save logo:", err);
      console.error("Error response:", err.response?.data);
      toast.error("Failed to update logo");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!platform?.logo) {
      toast.error("No logo to delete");
      return;
    }

    if (!window.confirm("Are you sure you want to delete the platform logo?")) {
      return;
    }

    setSaving(true);
    try {
      await deletePlatformLogo();
      toast.success("Logo deleted successfully");
      setPreviewUrl("");
      setFile(null);
      fetchPlatform();
    } catch (err) {
      toast.error("Failed to delete logo");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApplePassLogo = async () => {
    if (!platform?.applePassLogo) {
      toast.error("No logo to delete");
      return;
    }

    if (!window.confirm("Are you sure you want to delete the platform logo?")) {
      return;
    }

    setSaving(true);
    try {
      await deleteApplePassLogo();
      toast.success("Logo deleted successfully");
      setApplePassPreviewUrl("");
      setFile(null);
      fetchPlatform();
    } catch (err) {
      toast.error("Failed to delete logo");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCurrency = async () => {
    setSavingCurrency(true);
    try {
      await updatePlatformCurrency(selectedCurrency);
      setCurrency(selectedCurrency);
      toast.success("Currency updated successfully");
    } catch (err) {
      toast.error("Failed to update currency");
    } finally {
      setSavingCurrency(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const hasChanges = file !== null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Image size={28} className="text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-800 m-0">
              Platform Logo
            </h1>
          </div>
          <button
            onClick={fetchPlatform}
            className="h-10 w-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
            title="Refresh"
          >
            <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          Upload your platform logo. This logo will be displayed across your
          application.
        </p>

        {/* Logo Preview */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Current Logo
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center bg-gray-50 min-h-[200px]">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Platform Logo"
                className="max-h-40 max-w-full object-contain rounded"
              />
            ) : (
              <div className="text-center text-gray-400">
                <Image size={48} className="mx-auto mb-2" />
                <p>No logo uploaded</p>
              </div>
            )}
          </div>
        </div>

        {/* Upload Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Upload New Logo
          </label>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            className="hidden"
          />
          <button
            onClick={triggerFileInput}
            disabled={uploading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition disabled:opacity-50"
          >
            <Upload size={20} />
            {uploading ? "Uploading..." : "Click to select an image"}
          </button>
          <p className="text-sm text-gray-500 mt-2">
            Supported formats: PNG, JPG, GIF, SVG. Maximum size: 5MB
          </p>
        </div>

        {/* Selected File Info */}
        {file && (
          <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              Selected: <span className="font-medium">{file.name}</span> (
              {(file.size / 1024).toFixed(1)} KB)
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving || uploading || !hasChanges}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={20} />
            {saving || uploading ? "Saving..." : "Save Logo"}
          </button>
          <button
            onClick={handleDelete}
            disabled={saving || !platform?.logo}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 size={20} />
            Delete
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Image size={28} className="text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-800 m-0">
              Apple Pass Logo
            </h1>
          </div>
          <button
            onClick={fetchPlatform}
            className="h-10 w-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
            title="Refresh"
          >
            <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          Upload your Apple Pass logo. This logo will be displayed on Apple
          Wallet passes.
        </p>

        {/* Logo Preview */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Current Logo
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center bg-gray-50 min-h-[200px]">
            {applePassPreviewUrl ? (
              <img
                src={applePassPreviewUrl}
                alt="Apple Pass Logo"
                className="max-h-40 max-w-full object-contain rounded"
              />
            ) : (
              <div className="text-center text-gray-400">
                <Image size={48} className="mx-auto mb-2" />
                <p>No logo uploaded</p>
              </div>
            )}
          </div>
        </div>

        {/* Upload Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Upload New Logo
          </label>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleApplePassFileSelect}
            accept="image/*"
            className="hidden"
          />
          <button
            onClick={triggerFileInput}
            disabled={uploading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition disabled:opacity-50"
          >
            <Upload size={20} />
            {uploading ? "Uploading..." : "Click to select an image"}
          </button>
          <p className="text-sm text-gray-500 mt-2">
            Supported formats: PNG, JPG, GIF, SVG. Maximum size: 5MB
          </p>
        </div>

        {/* Selected File Info */}
        {file && (
          <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              Selected: <span className="font-medium">{file.name}</span> (
              {(file.size / 1024).toFixed(1)} KB)
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={saveApplePassLogo}
            disabled={saving || uploading || !hasChanges}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={20} />
            {saving || uploading ? "Saving..." : "Save Logo"}
          </button>
          <button
            onClick={handleDeleteApplePassLogo}
            disabled={saving || !platform?.logo}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 size={20} />
            Delete
          </button>
        </div>
      </div>

      {/* Currency Settings */}
     <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div className="flex items-center gap-3 mb-6">
          <DollarSign size={28} className="text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-800 m-0">Currency</h1>
        </div>
        <p className="text-gray-600 mb-6">
          Select the currency to display across your platform. All prices will use this currency symbol.
        </p>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Currency
          </label>
          <select
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {SUPPORTED_CURRENCIES.map(({ code, name }) => (
              <option key={code} value={code}>
                {getCurrencySymbol(code)} — {code} ({name})
              </option>
            ))}
          </select>
          {selectedCurrency && (
            <p className="text-sm text-gray-500 mt-2">
              Symbol preview: <span className="font-bold text-gray-800">{getCurrencySymbol(selectedCurrency)}</span>
            </p>
          )}
        </div>
        <button
          onClick={handleSaveCurrency}
          disabled={savingCurrency || selectedCurrency === (platform?.currency || "USD")}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={20} />
          {savingCurrency ? "Saving..." : "Save Currency"}
        </button>
      </div> 
    </div>
  );
};

export default PlatformLogo;
